import {cn} from '@/utils/common_functions';
import React, {useCallback, useState, FocusEvent, useMemo, useRef, useEffect} from 'react';
import {Text, TextVariants} from '../Text';
import {PhoneInput as ReactInternationalPhoneInput, PhoneInputProps as ReactInternationalPhoneInputProps, defaultCountries, ParsedCountry, FlagImage} from 'react-international-phone';
import {InputStateTypes} from '@/interface';
import Button from '../Button';
import {useSelector} from 'react-redux';
import {appCountries} from '@/services/redux/selectors/appSettingSelector';

interface PhoneInputProps {
  label?: string;
  value: string;
  country?: number|null;
  name?: string;
  onChange: (phone: string, country: number) => void;
  error?: string;
  touched?: boolean;
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  className?: string;
  wrapperClassName?: string;
  labelClassName?: string;
  inputClassName?: string;
  showOptionalLabel?:boolean;
  onBlur?: ReactInternationalPhoneInputProps['onBlur'];
  onFocus?: ReactInternationalPhoneInputProps['onFocus']; 
  lockCountry?: boolean;
  button?: {
    text: string;
    onClick: () => void;
    disabled: boolean;
    className?: string;
    textClassname?: string;
    isLoading?: boolean;
  };
}

export const PhoneInput: React.FC<PhoneInputProps> = props => {
  const {
    value = '',
    disabled = false,
    label,
    name,
    onChange,
    button,
    className = '',
    error = '',
    inputClassName = '',
    labelClassName = '',
    placeholder,
    required = false,
    touched = false,
    wrapperClassName = '',
    onBlur,
    onFocus,
    country,
    showOptionalLabel=false,
    lockCountry=false,
  } = props;

  // states
  const [inputState, setInputState] = useState<InputStateTypes>('default');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const countryList = useSelector(appCountries);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Create a mapping once for better performance
  const countryMapping = useMemo(() => {
    const labelToId: Record<string, number> = {};
    const idToIso: Record<number, string> = {};
    
    countryList.forEach(item => {
      const lowerLabel = item.label.toLowerCase();
      labelToId[lowerLabel] = item.id;
      
      // Find ISO code from defaultCountries
      const match = defaultCountries.find(([countryLabel]) => 
        countryLabel.toLowerCase() === lowerLabel
      );
      if (match) {
        idToIso[item.id] = match[1];
      }
    });
    
    return { labelToId, idToIso };
  }, [countryList]);

  // Get default country ISO code
  const defaultCountryIso = useMemo(() => {
    return country ? (countryMapping.idToIso[country] || 'in') : 'in';
  }, [country, countryMapping.idToIso]);

  // Get selected country info for custom dropdown
  const selectedCountryInfo = useMemo(() => {
    if (!country) return null;
    const countryData = countryList.find(c => c.id === country);
    if (!countryData) return null;
    const isoCode = countryMapping.idToIso[country];
    return { ...countryData, iso2: isoCode };
  }, [country, countryList, countryMapping.idToIso]);

  // Filter countries based on search query
  const filteredCountries = useMemo(() => {
    if (!searchQuery) return countryList;
    const query = searchQuery.toLowerCase();
    return countryList.filter(c => 
      c.label.toLowerCase().includes(query) || 
      (countryMapping.idToIso[c.id] || '').toLowerCase().includes(query)
    );
  }, [countryList, searchQuery, countryMapping.idToIso]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking on the dropdown or the country selector button
      if (dropdownRef.current?.contains(target) || target.closest('.react-international-phone-country-selector')) {
        return;
      }
      setIsDropdownOpen(false);
      setSearchQuery('');
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDropdownOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isDropdownOpen]);

  // Handle country selection from custom dropdown
  const handleCountrySelect = useCallback((selectedCountry: typeof countryList[0]) => {
    setIsDropdownOpen(false);
    setSearchQuery('');
    // Clear phone value when country changes, update country
    onChange('', selectedCountry.id);
  }, [onChange]);

  // Handle click on the wrapper to detect country button clicks for custom search dropdown
  const handleWrapperClick = useCallback((e: React.MouseEvent) => {
    if (lockCountry || disabled) return;
    
    // Check if clicked on the country selector button
    const target = e.target as HTMLElement;
    if (target.closest('.react-international-phone-country-selector')) {
      e.preventDefault();
      e.stopPropagation();
      setIsDropdownOpen(prev => !prev);
    }
  }, [lockCountry, disabled]);

  /**
   * a utility function to update input state with a check to disabled logic,
   */
  const updateInputState = useCallback(
    (state: InputStateTypes) => {
      if (disabled) {
        return;
      }
      setInputState(state);
    },
    [disabled],
  );

  /**
   * utility wrapper for onBlur handler, such that if also updates the input state locally
   */
  function onBlurHandler(e: FocusEvent<HTMLInputElement>): void {
    updateInputState(error && touched ? 'error' : 'default');
    onBlur?.(e);
  }

  /**
   * utility wrapper for onFocus handler, such that if also updates the input state locally
   */
  function onFocusHandler(e: FocusEvent<HTMLInputElement>): void {
    updateInputState('focus');
    onFocus?.(e);
  }

  const handleChange = useCallback((phone: string, info: { country: ParsedCountry; inputValue: string }) => {
    const countryLabel = info.country.name.toLowerCase();
    const countryId = countryMapping.labelToId[countryLabel];
    
    if (countryId !== undefined) {
      // Skip the first call on mount when component initializes with dial code only
      if (!isInitialized && phone === `+${info.country.dialCode}`) {
        setIsInitialized(true);
        return;
      }
      
      setIsInitialized(true);
      
      // If phone only contains dial code (country was switched), clear the value
      if (phone === `+${info.country.dialCode}`) {
        onChange('', countryId); // Clear phone, update country
      } else {
        onChange(phone, countryId); // Update both phone and country
      }
    }
  }, [countryMapping.labelToId, onChange, isInitialized]);

  return (
    <div className={cn('flex relative flex-col gap-1 lg:gap-2', className)}>
      {label && (
        <Text className={cn('!font-FigtreeSemiBold inline-flex gap-1 text-ui_blue', labelClassName)} variant="body">
          {label}
          {required && <p className="text-rejection">*</p>}
          {showOptionalLabel && <p className='font-FigtreeLight text-xs self-center mt-1'>(optional)</p>}
        </Text>
      )}
      <div
        onClick={handleWrapperClick}
        className={cn(
          'border-2 flex border-ui_input_border grow relative text-base rounded-lg items-center disabled:cursor-not-allowed',
          inputState === 'error' && 'border-rejection',
          inputState === 'focus' && 'border-black',
          disabled && 'border-ui_disabled',
          TextVariants.body,
          wrapperClassName,
        )}>
        
        {/* Search Dropdown - positioned relative to wrapper */}
        {isDropdownOpen && !lockCountry && (
          <div ref={dropdownRef} className="absolute top-full left-0 mt-1 w-[280px] bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[300px] flex flex-col">
            {/* Search Input */}
            <div className="p-2 border-b border-gray-200">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search countries..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-none focus:border-black"
              />
            </div>
            
            {/* Countries List */}
            <div className="overflow-y-auto">
              {filteredCountries.length > 0 ? (
                filteredCountries.map(countryItem => {
                  const iso2 = countryMapping.idToIso[countryItem.id];
                  const dialCode = defaultCountries.find(([, code]) => code === iso2)?.[2];
                  return (
                    <button
                      key={countryItem.id}
                      type="button"
                      onClick={() => handleCountrySelect(countryItem)}
                      className={cn(
                        'w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0 transition-colors',
                        country === countryItem.id && 'bg-blue-50',
                      )}>
                      <FlagImage iso2={iso2 || 'in'} size={18} />
                      <span className="font-FigtreeMedium text-sm flex-1 truncate">{countryItem.label}</span>
                      <span className="text-sm text-gray-500 font-FigtreeMedium">+{dialCode}</span>
                    </button>
                  );
                })
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">No countries found</div>
              )}
            </div>
          </div>
        )}

        <ReactInternationalPhoneInput
          name={name}
          value={value}
          placeholder={placeholder}
          className={cn('grow w-full', '[&_.react-international-phone-country-selector-dropdown]:!hidden')}
          disabled={disabled}
          defaultCountry={defaultCountryIso}
          countrySelectorStyleProps={{
            buttonClassName: cn('p-2 h-full'),
            dropdownStyleProps: {
              className: cn('absolute', '!hidden'),
              listItemClassName: 'no-flag',
            },
          }}
          onChange={handleChange}
          onBlur={onBlurHandler}
          onFocus={onFocusHandler}
          disableCountryGuess={lockCountry}
          forceDialCode={true}
          showDisabledDialCodeAndPrefix={lockCountry}
        />
        {button && (
          <Button
            onClick={button.onClick}
            disabled={disabled || button.disabled}
            className={cn('h-full w-[130px] min-w-fit px-0 rounded-l-none', button.className)}
            title={button.text ?? ''}
            textClassName={cn('text-lg font-Medium', button.textClassname)}
            isLoading={button.isLoading}
          />
        )}
      </div>

      {!disabled && touched && error && <Text className="text-rejection text-sm">{error}</Text>}
    </div>
  );
};